import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Kicker } from "@/components/Section";
import Reveal from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Kontakt = () => {
  const { t, lang } = useLang();
  const k = t.kontakt;
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(k.sent);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Error / Fehler");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#1A1A1A] border border-white/10 text-white p-4 font-body focus:border-[#ff0033] outline-none transition-colors placeholder:text-neutral-600";

  return (
    <div data-testid="kontakt-page">
      <section className="pt-36 md:pt-44 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal direction="up">
          <Kicker>{k.kicker}</Kicker>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl uppercase tracking-tight leading-none text-white mt-4">
            {k.title}
          </h1>
          <p className="text-neutral-400 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">{k.intro}</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
          <Reveal direction="left" className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="contact-name-input" required placeholder={k.name} value={form.name} onChange={update("name")} className={inputCls} />
                <input data-testid="contact-email-input" required type="email" placeholder={k.email} value={form.email} onChange={update("email")} className={inputCls} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="contact-phone-input" placeholder={k.phone} value={form.phone} onChange={update("phone")} className={inputCls} />
                <input data-testid="contact-subject-input" placeholder={k.subject} value={form.subject} onChange={update("subject")} className={inputCls} />
              </div>
              <textarea data-testid="contact-message-input" required rows={6} placeholder={k.message} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-4 text-lg hover:bg-[#cc0029] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-auto"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : k.send}
              </button>
            </form>
          </Reveal>

          <Reveal direction="right" delay={0.1} className="lg:col-span-5">
            <div className="bg-[#0F0F0F] border border-white/10 p-8 h-full">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin size={18} className="text-[#ff0033]" />
                  <span className="font-mono-lab text-xs tracking-[0.2em] text-neutral-500 uppercase">{k.addressTitle}</span>
                </div>
                <p className="text-neutral-200 whitespace-pre-line leading-relaxed">{k.address}</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Mail size={18} className="text-[#ff0033]" />
                  <span className="font-mono-lab text-xs tracking-[0.2em] text-neutral-500 uppercase">{k.emailLabel}</span>
                </div>
                <a href={`mailto:${k.emailValue}`} className="text-neutral-200 hover:text-[#ff0033] transition-colors break-all">
                  {k.emailValue}
                </a>
              </div>
              <div className="mt-10 pt-8 border-t border-white/10">
                <span className="font-mono-lab text-xs tracking-[0.2em] text-[#ff0033] uppercase">{t.footer.poweredBy}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Kontakt;
